<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { appKind, workspaceKinds } from 'dashboard/helper/reelWorkspace.mjs';
import { isAConversationRoute } from 'dashboard/helper/routeHelpers';
import { useSidebarContext } from './provider';

defineProps({ collapsed: { type: Boolean, default: false } });
const emit = defineEmits(['opened', 'needsExpansion']);
const { t } = useI18n();
const route = useRoute();
const { accountScopedRoute } = useAccount();
const apps = useMapGetter('dashboardApps/getRecords');
const chat = useMapGetter('getSelectedChat');
const accountId = useMapGetter('getCurrentAccountId');
const hint = ref('');
const activeKind = ref('conversations');
const { isAllowed } = useSidebarContext();
const inConversations = computed(() => isAConversationRoute(route.name, true));
watch(() => [route.name, route.params.conversation_id, route.params.accountId], () => { activeKind.value = 'conversations'; hint.value = ''; });
const syncWorkspace = ({ detail }) => {
  if (String(detail?.accountId) !== String(accountId.value)
    || String(detail?.conversationId) !== String(route.params.conversation_id)
    || String(detail?.conversationId) !== String(chat.value?.id)) return;
  activeKind.value = ['conversations', ...workspaceKinds].includes(detail.kind) ? detail.kind : '';
};
onMounted(() => window.addEventListener('reelnearby:workspace-changed', syncWorkspace));
onUnmounted(() => window.removeEventListener('reelnearby:workspace-changed', syncWorkspace));
const explain = message => { hint.value = message; emit('needsExpansion'); };
const icons = { sales: 'i-lucide-chart-no-axes-combined', accounts: 'i-lucide-smartphone', training: 'i-lucide-graduation-cap' };
const entries = computed(() => workspaceKinds.map(kind => ({
  kind, icon: icons[kind], app: apps.value.find(app => appKind(app, location.origin) === kind),
})));
const openWorkspace = entry => {
  if (!entry.app) { explain(t('REEL_UI.APP_UNAVAILABLE')); return; }
  if (!chat.value?.id || String(route.params.conversation_id) !== String(chat.value.id)
    || String(chat.value.account_id) !== String(accountId.value)) {
    explain(t('REEL_UI.CHOOSE_CUSTOMER')); return;
  }
  hint.value = '';
  window.dispatchEvent(new CustomEvent('reelnearby:open-dashboard-app', { detail: {
    appId: entry.app.id, accountId: accountId.value, conversationId: chat.value.id,
  } }));
  emit('opened');
};
const openMessages = event => {
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  if (!chat.value?.id || String(route.params.conversation_id) !== String(chat.value.id)
    || String(chat.value.account_id) !== String(accountId.value)) return;
  event.preventDefault();
  hint.value = '';
  window.dispatchEvent(new CustomEvent('reelnearby:open-dashboard-app', { detail: {
    kind: 'conversations', accountId: accountId.value, conversationId: chat.value.id,
  } }));
  emit('opened');
};
</script>

<template>
  <section v-if="isAllowed(accountScopedRoute('home'))" class="flex flex-col gap-1 px-2 pb-4 border-b border-n-weak" :aria-label="t('REEL_UI.WORKSPACE')" data-reel-workspace>
    <RouterLink :to="accountScopedRoute('reel_dashboard')" data-overview-link class="flex items-center gap-3 min-h-11 px-3 rounded-lg text-n-slate-12 hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-n-blue-11" :class="{ 'justify-center px-1': collapsed, 'bg-n-iris-3 text-n-blue-11 font-semibold': route.name === 'reel_dashboard' }" :aria-current="route.name === 'reel_dashboard' ? 'page' : undefined" :title="t('REEL_UI.OVERVIEW.TITLE')" @click="emit('opened')">
      <span class="i-lucide-layout-dashboard size-5 shrink-0" aria-hidden="true" />
      <span v-if="!collapsed">{{ t('REEL_UI.OVERVIEW.TITLE') }}</span>
    </RouterLink>
    <RouterLink :to="chat?.id && String(route.params.conversation_id) === String(chat.id) ? route.fullPath : accountScopedRoute('home')" class="flex items-center gap-3 min-h-10 px-3 rounded-lg text-n-slate-12 hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-n-blue-11" :class="{ 'justify-center px-1': collapsed, 'bg-n-iris-3 text-n-blue-11 font-semibold': inConversations && activeKind === 'conversations' }" :aria-current="inConversations && activeKind === 'conversations' ? 'page' : undefined" :title="t('REEL_UI.CONVERSATIONS')" @click="openMessages">
      <span class="i-lucide-messages-square size-5 shrink-0" aria-hidden="true" />
      <span v-if="!collapsed">{{ t('REEL_UI.CONVERSATIONS') }}</span>
    </RouterLink>
    <button v-for="entry in entries" :key="entry.kind" type="button" :data-workspace="entry.kind" :title="t(`REEL_UI.${entry.kind.toUpperCase()}`)" :aria-current="inConversations && activeKind === entry.kind ? 'page' : undefined" class="flex items-center gap-3 min-h-10 px-3 rounded-lg text-n-slate-11 text-left hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-n-blue-11" :class="{ 'justify-center px-1': collapsed, 'bg-n-iris-3 !text-n-blue-11': inConversations && activeKind === entry.kind }" @click="openWorkspace(entry)">
      <span :class="entry.icon" class="size-5 shrink-0 text-n-blue-11" aria-hidden="true" />
      <span v-if="!collapsed" class="font-medium">{{ t(`REEL_UI.${entry.kind.toUpperCase()}`) }}</span>
    </button>
    <p v-if="hint" class="text-xs leading-5 text-n-slate-11 px-3 pt-2 m-0" :class="{ 'sr-only': collapsed }" role="status">{{ hint }}</p>
    <p v-else-if="!collapsed" class="text-xs leading-5 text-n-slate-11 px-3 pt-2 m-0">{{ t('REEL_UI.CONTEXT_HINT') }}</p>
  </section>
</template>
